import { useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { uploadUserFile } from '../../../../../service/user'

interface UseImageUploadProps {
  onUploadComplete?: (file: File) => void
  onTabSwitch?: (tab: 'uploaded') => void
  onRefreshFiles?: () => void
}

export const useImageUpload = ({
  onUploadComplete,
  onTabSwitch,
  onRefreshFiles,
}: UseImageUploadProps = {}) => {
  const [isUploading, setIsUploading] = useState(false)
  const queryClient = useQueryClient()

  const uploadFile = useCallback(
    async (file: File) => {
      if (!file || !file.type.startsWith('image/')) {
        throw new Error('유효한 이미지 파일을 선택해주세요.')
      }

      try {
        setIsUploading(true)
        
        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 Starting file upload:', {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type
          })
        }
        
        const uploadedFile = await uploadUserFile({
          file,
          fileType: 'direct_upload'
        })

        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Upload successful:', uploadedFile)
        }
        
        // 캐시 무효화 및 새로고침
        await queryClient.invalidateQueries({
          queryKey: ['user', 'files', 'direct_upload'],
        })
        
        // 콜백 호출
        onTabSwitch?.('uploaded')
        onRefreshFiles?.()
        onUploadComplete?.(file)

        return uploadedFile
      } catch (error) {
        console.error('❌ Upload failed:', error)
        throw new Error('파일 업로드에 실패했습니다. 다시 시도해주세요.')
      } finally {
        setIsUploading(false)
      }
    },
    [queryClient, onUploadComplete, onTabSwitch, onRefreshFiles]
  )

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault()
      
      const files = Array.from(e.dataTransfer.files)
      const imageFile = files.find((file) => file.type.startsWith('image/'))

      if (imageFile) {
        await uploadFile(imageFile)
      }
    },
    [uploadFile]
  )

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        await uploadFile(file)
      }
    },
    [uploadFile]
  )

  return {
    isUploading,
    uploadFile,
    handleDrop,
    handleFileSelect,
  }
}